import { Component, ErrorInfo, ReactNode, Suspense, useEffect } from "react";
import { observer } from "mobx-react";
import { History } from "history";
import { portfolioCompaniesUrl, portfolioUrl } from "./utils/navigation";
import { LoadingIndicator } from "./components-v2/LoadingIndicator";
import { ErrorPage } from "./ErrorPage";
import { toSupportedLanguage } from "./i18n/languages";
import LoginPage from "./Login/LoginPage";
import { changeLanguageAction } from "./i18n/i18nDomain";
import {
  useDispatch,
  useI18n,
  useRootStore,
  useUserPrefs,
  useUserStore,
} from "./stores/RootStoreContext";
import { TFunction } from "./i18n/tt";
import { useQuery } from "./utils/queryParams";
import { FullScreenLayout } from "./components-v2/FullScreenLayout";
import { getFullName } from "./utils/Roles";
import { Dialogs } from "./dialogs/dialogComponents";
import { RoutesView } from "./routes/RoutesView";
import { Announcement } from "./announcement/announcementComponents";

type ErrorBoundaryProps = {
  t: TFunction;
  children: ReactNode;
  onUncaughtError: (error: Error, errorInfo: ErrorInfo) => void;
};

type ErrorBoundaryState = { error: Error | null };

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onUncaughtError(error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return <ErrorPage backUrl={portfolioUrl} />;
    }

    return this.props.children;
  }
}

type Props = {
  history: History;
  onUncaughtError: ErrorBoundaryProps["onUncaughtError"];
};

const App = observer(function App({ history, onUncaughtError }: Props) {
  const rootStore = useRootStore();
  const userStore = useUserStore();
  const userPrefs = useUserPrefs();
  const i18n = useI18n();
  const dispatch = useDispatch();
  const route = rootStore.route;
  const missingAccess =
    !userStore.isUserLoading &&
    userStore.hasOnlyShareholderAccess &&
    !route?.data?.allowShareholder &&
    !route?.data?.allowAnonymous;
  const isLoginRequired =
    !userStore.isUserLoading && userStore.isLoginRequired && !route?.data?.allowAnonymous;
  const announcement = <Announcement userPrefs={userPrefs} announcement={rootStore.announcement} />;

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    if (userStore.user && userStore.isAuthenticated && !queryParams.has("lang")) {
      dispatch(changeLanguageAction(toSupportedLanguage(userStore.user.language)));
    }
  }, [userStore.user, userStore.isAuthenticated, dispatch, history]);

  useEffect(() => {
    if (userStore.companyInfo?.company.whitelabel) {
      document.documentElement.style.setProperty(
        "--color-primary",
        userStore.companyInfo?.company.whitelabel.primaryColor,
      );
      document.documentElement.style.setProperty(
        "--color-primary-contrast",
        userStore.companyInfo?.company.whitelabel.primaryContrastColor,
      );
      document.documentElement.style.setProperty(
        "--color-primary-light",
        userStore.companyInfo?.company.whitelabel.primaryLightColor,
      );
      document.documentElement.style.setProperty(
        "--color-primary-dark",
        userStore.companyInfo?.company.whitelabel.primaryDarkColor,
      );
    }
  }, [userStore.companyInfo?.company.whitelabel]);

  useEffect(() => {
    if (missingAccess) {
      dispatch([{ type: "navigate", url: portfolioCompaniesUrl, replace: true }]);
    }
  }, [missingAccess, dispatch]);

  if (userStore.isUserLoading) {
    return <LoadingIndicator fullscreen />;
  }

  if (userStore.isLoadError) {
    return <ErrorPage backUrl={portfolioCompaniesUrl} header={announcement} />;
  }

  return (
    <Suspense fallback={<LoadingIndicator fullscreen />}>
      <ErrorBoundary onUncaughtError={onUncaughtError} t={i18n.t}>
        <FullScreenLayout
          appBar={
            <>
              <RolePreviewBanner />
              <Impersonation />
              {announcement}
            </>
          }
          centerPanelContainerProps={{ className: "tw-relative" }}
          centerPanel={isLoginRequired ? <LoginPage /> : <RoutesView route={route} />}
        />
        {!isLoginRequired && <Dialogs store={rootStore} />}
      </ErrorBoundary>
    </Suspense>
  );
});

function RolePreviewBanner() {
  const roleId = useQuery().get("roleId");
  const role = useUserStore().userRole;
  return roleId ? (
    <div className={"bg-accent1 color-accent2-contrast tw-p-2 text-center font-bold"}>
      You are previewing "{role.customRoleName}"
    </div>
  ) : null;
}

function Impersonation() {
  const userStore = useUserStore();
  const user = userStore.user;
  return user?.isImpersonated ? (
    <div className={"bg-accent2 color-accent2-contrast tw-p-1 text-center"}>
      You are impersonating {getFullName(user)}{" "}
      <a
        href={"/api/superadmin/logout-impersonated-user"}
        className={"color-accent2-contrast visited:color-accent2-contrast"}
      >
        Back to superadmin
      </a>
    </div>
  ) : null;
}

export default App;
